/**
 * @name CartQuantityHandling.Entry.js
 *
 * @author Anchor Group : Sam Gagliardi <sgagliardi@anchorgroup.tech>
 * 
 * @since 2023-1-26
 * 
 * @file Auto-adjust cart quantities as need on page load and on quantity change for the cart and checkout.
 */

define('CartQuantityHandling.Entry',
[
    'Utils',
    'GlobalViews.Message.View',
    'Profile.Model'
], function (
    Utils,
    GlobalViewsMessageView,
    ProfileModel
) {
    'use strict';

    //define globals
    var Cart,
        Layout,
        Environment,
        Configuration;

    /**
     * Validate that cart items are not over any quantity limits.
     * If any are, update them and display a message.
     * @param {LiveOrder.Model} cartModel 
     * @param {integer} priceLevel 
     * @param {object} self 
     * @param {Backbone.View} view 
     */
    function validateCartItems (cartModel, priceLevel, self, view) {

        var lines = cartModel.get("lines").models;

        var customerInventoryLimit = getCustomerInventoryLimit(priceLevel, Configuration);
        var displayMessage = false;

        for (const line of lines) {

            var item = line.get("item");

            if (
                line.get("quantity") > customerInventoryLimit || //quantity is over the inventory limit
                line.get("quantity") > item.get("quantityavailable") //quantity is over the available limit
            ) {
                adjustItemQuantity(line, item.get("quantityavailable"), view);
                displayMessage = true;
                continue;
            }

        }

        if (displayMessage)
            self.displayMessage();
    }

    /**
     * Return the inventory limit based on the price level configuration settings.
     * @param {integer} priceLevel 
     * @param {Configuration object} Configuration 
     * @returns {integer}
     */
    function getCustomerInventoryLimit (priceLevel, Configuration) {
        try {
            return Configuration.pricelevelinventorydisplay.find(display => display.pricelevel == priceLevel).maximum;
        } catch (error) {
            return 720;
        }
    }

    /**
     * Display a message when cart items have been automatically updated.
     */
    function displayMessage () {

        var message = Configuration.CartQuantityHandling.popUpMessage;
        message = message.split('.').map(mes => mes + '.');
        message.pop();

        setTimeout(() => {
            var view = new GlobalViewsMessageView({
                message: message,
                closable: false,
                type: 'cart-handling'
            });
            Layout.showContent(view, {showInModal: true})
        }, 500);
    }

    /**
     * Adjust the quantity of a line in the cart, then update the perLines attribute on a view.
     * @param {TransactionLine.Model} line 
     * @param {integer} newQuantity 
     * @param {Backbone.View} view 
     */
    function adjustItemQuantity (line, newQuantity, view) {

        var oldQuantity = line.get('quantity');

        Cart.updateLine({
            line: {
                internalid: line.get('internalid'),
                quantity: newQuantity
            }
        }).then(function () {
            if (view.prevLines) {
                var prevLineQty = view.prevLines.find(prevLine => prevLine.internalid == line.get('item').get('internalid'));
                if (prevLineQty) {
                    prevLineQty.quantity = oldQuantity;
                }
                else {
                    view.prevLines.push({
                        internalid: line.get('item').get('internalid'),
                        quantity: oldQuantity
                    })
                }
            }
            else {
                view.prevLines = [{
                    internalid: line.get('item').get('internalid'),
                    quantity: oldQuantity
                }]
            }
            view.render();
        });
    }

    return {
        mountToApp: function mountToApp(container) {

            var self = this;

            //Get components
            Cart = container.getComponent("Cart");
            Layout = container.getComponent('Layout');
            Environment = container.getComponent('Environment');
            Configuration = Environment.getConfig('');
 
            //get price level
            var profile = ProfileModel.getInstance();
            var priceLevel = profile.get('pricelevel');

            if (Utils.isInCheckout()) {
                //load in dependent views for checkout
                var OrderWizardModuleCartItemsShip = require('OrderWizard.Module.CartItems.Ship');
                var TransactionLineViewsCellNavigableView = require('Transaction.Line.Views.Cell.Navigable.View').TransactionLineViewsCellNavigableView;

                //A theme change was made to transaction_line_views_cell_navigable.tpl to display the "Previous Quantity"
                //Grab the previous quantities from the OrderWizard.Module.CartItems.Ship and update context for the line
                TransactionLineViewsCellNavigableView.prototype.getContext = _.wrap(TransactionLineViewsCellNavigableView.prototype.getContext, function (fn) {
                    var context = fn.apply(this, _.toArray(arguments).slice(1));
                    var prevLines = this.parentView.parentView.prevLines;

                    if (prevLines) {
                        for (const prevLine of prevLines) {
                            if (prevLine.internalid == context.itemId) {
                                context.previousQuantity = prevLine.quantity;
                            }
                        }
                    }

                    if (context.quantity != context.previousQuantity)
                        context.showRed = Configuration.CartQuantityHandling.redMessage;

                    return context;
                });

                //Validate cart contents as needed. 
                OrderWizardModuleCartItemsShip.prototype.getContext = _.wrap(OrderWizardModuleCartItemsShip.prototype.getContext, function (fn) {
                    var context = fn.apply(this, _.toArray(arguments).slice(1));

                    if (!this.cartItemsValidated) {
                        if(this.model.get('lines').length > 0) {
                            self.validateCartItems(this.model, priceLevel, self, this);
                            this.cartItemsValidated = true;
                        }
                    }

                    return context;
                });

            }
            else if (Utils.isInShopping()) {
                //load in dependent views for shopping
                var CartItemSummaryView = require('Cart.Item.Summary.View');
                var CartDetailedView = require('Cart.Detailed.View');

                //A theme change was made to cart_item_summary.tpl to display the "Previous Quantity".
                //Grab the previous quantities from the Cart.Detailed.View and update context for the line.
                CartItemSummaryView.prototype.getContext = _.wrap(CartItemSummaryView.prototype.getContext, function (fn) {
                    var context = fn.apply(this, _.toArray(arguments).slice(1));
                    var prevLines = this.parentView.parentView.parentView.prevLines;
    
                    if (prevLines) {
                        for (const prevLine of prevLines) {
                            if (prevLine.internalid == context.line.get('item').get('internalid')) {
                                context.previousQuantity = prevLine.quantity;
                            }
                        }
                    }
    
                    if (context.line.get('quantity') != context.previousQuantity)
                        context.showRed = Configuration.CartQuantityHandling.redMessage;
    
                    return context;
                });
    
                //Reset the validation flag on quantity change.
                CartDetailedView.prototype.updateItemQuantity = function updateItemQuantity(e) {
                    const $form = this.getItemForm(e);
                    const options = ($form).serializeObject();
                    let { internalid } = options;
                    let line = this.model.get('lines').get(internalid);
                    const $input = $form.find('[name="quantity"]');
                    const validInput = this.validInputValue($input[0]);
            
                    if (!line || this.isRemoving) {
                        return;
                    }
            
                    if (!validInput || options.quantity) {
                        const new_quantity = parseInt(options.quantity, 10);
                        const item = line.get('item');
                        const min_quantity = item
                            ? item.get('_minimumQuantity', true)
                            : line.get('minimumquantity');
                        const max_quantity = item
                            ? item.get('_maximumQuantity', true)
                            : line.get('maximumquantity');
                        const current_quantity = parseInt(line.get('quantity'), 10);
            
                        let new_valid_quantity = new_quantity >= min_quantity ? new_quantity : min_quantity;
                        new_valid_quantity =
                            !!max_quantity && new_quantity > max_quantity
                                ? max_quantity
                                : new_quantity > min_quantity
                                ? new_quantity
                                : min_quantity;
            
                        $input.val(new_valid_quantity);
            
                        if (new_quantity !== current_quantity) {
                            $input.val(new_valid_quantity).prop('disabled', true);
                            line.set('quantity', new_valid_quantity);
            
                            const invalid = line.validate();
            
                            if (!invalid) {
                                const update_promise = this.model.updateLine(line, true);
                                this.disableElementsOnPromise(update_promise, `#${internalid} button`);
            
                                update_promise
                                    .fail((jqXhr) => {
                                        const result = JSON.parse(jqXhr.responseText);
                                        const { errorDetails } = result;
            
                                        if (errorDetails.newLineId) {
                                            internalid = errorDetails.newLineId;
                                            // we use the new line
                                            line = this.model.get('lines').get(internalid);
                                        }
            
                                        // revert quantity to the last quantity
                                        line.set('quantity', current_quantity);
                                        this.render();
                                    })
                                    .always(function() {
                                        $input.prop('disabled', false);
                                    });
                            } else {
                                const placeholder = this.$(`#${internalid} [data-type="alert-placeholder"]`);
                                placeholder.empty();
            
                                _.each(invalid, function(value) {
                                    const global_view_message = new GlobalViewsMessageView({
                                        message: value,
                                        type: 'error',
                                        closable: true
                                    });
            
                                    placeholder.append(global_view_message.render().$el.html());
                                });
            
                                $input.prop('disabled', false);
                                line.set('quantity', current_quantity);
                            }
                            
                            //CUSTOM CODE STARTS
                            this.cartItemsValidated = false;
                            //CUSTOM CODE ENDS
                        }
                    }
                }
                
                //Validate cart contents as needed. 
                CartDetailedView.prototype.getContext = _.wrap(CartDetailedView.prototype.getContext, function (fn) {
                    var context = fn.apply(this, _.toArray(arguments).slice(1));
    
                    if (!this.cartItemsValidated) {
                        if(this.model.get('lines').length > 0) {
                            self.validateCartItems(this.model, priceLevel, self, this);
                            this.cartItemsValidated = true;
                        }
                    }
    
                    return context;
                });

            }
        },
        validateCartItems: validateCartItems,
        displayMessage: displayMessage

    };
});
